import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import ClienteBuscaModal from './ClienteBuscaModal';

const UsuarioVinculoModal = ({
  isOpen,
  vinculo,
  usuario,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    cnpj: '',
    cpf: '',
    tipo: '',
    situacao: 'ATIVO',
  });
  const [clienteVisual, setClienteVisual] = useState({
    cnpj: '',
    nome: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [isBuscaModalOpen, setIsBuscaModalOpen] = useState(false);

  const token = localStorage.getItem('token');

  // Carrega lista de clientes
  useEffect(() => {
    if (isOpen) {
      axios
        .get(`${API_BASE_URL.CLIENTE}/all`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(result => {
          setClientes(result.data);
        })
        .catch(error => {
          console.error('Erro ao carregar clientes:', error);
          setError('Erro ao carregar lista de clientes.');
        });
    }
  }, [isOpen, token]);

  // Atualiza formData ao abrir modal ou mudar vínculo
  useEffect(() => {
    if (isOpen) {
      if (vinculo) {
        setFormData({
          cnpj: vinculo.cnpj || '',
          cpf: vinculo.cpf || usuario?.cpf || '',
          tipo: vinculo.tipo || '',
          situacao: vinculo.situacao || 'ATIVO',
        });
        // Buscar dados visuais do cliente
        if (vinculo.cnpj && clientes.length > 0) {
          const cliente = clientes.find(c => c.cnpj === vinculo.cnpj);
          setClienteVisual({
            cnpj: vinculo.cnpj,
            nome: cliente ? cliente.nome : '',
          });
        }
        setIsEditing(true);
      } else {
        setFormData({
          cnpj: '',
          cpf: usuario?.cpf || '',
          tipo: '',
          situacao: 'ATIVO',
        });
        setClienteVisual({
          cnpj: '',
          nome: '',
        });
        setIsEditing(false);
      }
      setError(null);
    }
  }, [isOpen, vinculo, usuario, clientes]);

  const handleInputChange = useCallback(event => {
    const { name, value } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  }, []);

  const handleOpenBuscaModal = () => {
    setIsBuscaModalOpen(true);
  };

  const handleCloseBuscaModal = () => {
    setIsBuscaModalOpen(false);
  };

  const handleClienteSelect = cliente => {
    setFormData(prevData => ({
      ...prevData,
      cnpj: cliente.cnpj,
    }));
    setClienteVisual({
      cnpj: cliente.cnpj,
      nome: cliente.nome,
    });
  };

  const validateForm = useCallback(() => {
    if (!formData.cnpj.trim()) {
      setError('Selecione um cliente/entidade.');
      return false;
    }
    if (!formData.cpf.trim()) {
      setError('CPF do usuário é obrigatório.');
      return false;
    }
    if (!formData.tipo) {
      setError('Tipo de vínculo é obrigatório.');
      return false;
    }
    if (!formData.situacao) {
      setError('Situação é obrigatória.');
      return false;
    }
    return true;
  }, [formData]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        await axios.post(`${API_BASE_URL.USUARIO_BIND}/save`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (onSuccess) {
          onSuccess(formData, isEditing);
        }
        onClose();
      } catch (error) {
        console.error('Erro ao salvar vínculo:', error);
        setError(
          error.response?.data?.message ||
            'Erro ao salvar vínculo. Tente novamente.'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validateForm, token, onSuccess, onClose, isEditing]
  );

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const getClienteNome = cnpj => {
    const cliente = clientes.find(c => c.cnpj === cnpj);
    return cliente ? cliente.nome : '';
  };

  return (
    <>
      <Modal show={isOpen} onHide={handleClose} size="lg" backdrop="static">
        <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
          <h6 className="modal-title text-center m-0">
            <i
              className={`bi ${
                isEditing ? 'bi-link-45deg' : 'bi-plus-circle-dotted'
              } me-2`}
            ></i>
            {isEditing ? 'Editar Vínculo' : 'Novo Vínculo'}
          </h6>
          <button
            className="btn btn-link text-white p-0 mb-0"
            onClick={handleClose}
            title="Fechar"
            style={{ fontSize: '0.9rem', textDecoration: 'none' }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}
            <Row className="g-2">
              <Col md={4}>
                <Form.Group className="mb-1">
                  <Form.Label>CNPJ *</Form.Label>
                  <div className="d-flex gap-1">
                    <Form.Control
                      type="text"
                      value={clienteVisual.cnpj}
                      size="sm"
                      readOnly
                      placeholder={
                        isEditing
                          ? 'Cliente vinculado'
                          : 'Clique em buscar para selecionar'
                      }
                      className="bg-light"
                    />
                    {!isEditing && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleOpenBuscaModal}
                        title="Buscar cliente/entidade"
                      >
                        <i className="bi bi-search"></i>
                      </Button>
                    )}
                  </div>
                </Form.Group>
              </Col>

              <Col md={8}>
                <Form.Group className="mb-1">
                  <Form.Label>Nome da Entidade</Form.Label>
                  <Form.Control
                    type="text"
                    value={clienteVisual.nome}
                    size="sm"
                    readOnly
                    placeholder="Selecione um cliente"
                    className="bg-light"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-1">
                  <Form.Label>Tipo de Vínculo *</Form.Label>
                  <Form.Select
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleInputChange}
                    size="sm"
                    required
                  >
                    <option value="">Selecione o tipo...</option>
                    <option value="ADMIN">ADMINISTRADOR</option>
                    <option value="USER">USUÁRIO</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-1">
                  <Form.Label>Situação *</Form.Label>
                  <Form.Select
                    name="situacao"
                    value={formData.situacao}
                    onChange={handleInputChange}
                    size="sm"
                    required
                  >
                    <option value="ATIVO">ATIVO</option>
                    <option value="INATIVO">INATIVO</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Salvando...
                </>
              ) : isEditing ? (
                'Atualizar'
              ) : (
                'Salvar'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ClienteBuscaModal
        isOpen={isBuscaModalOpen}
        onClose={handleCloseBuscaModal}
        onSelect={handleClienteSelect}
      />
    </>
  );
};

UsuarioVinculoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  vinculo: PropTypes.object,
  usuario: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default UsuarioVinculoModal;
